import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import {
  useCalendarFeed,
  useIssueCalendarFeed,
  useRevokeCalendarFeed
} from './hooks';

export function TenantFeedSettings() {
  const { data: feed, isLoading } = useCalendarFeed();
  const issue = useIssueCalendarFeed();
  const revoke = useRevokeCalendarFeed();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!feed?.url) return;
    await navigator.clipboard.writeText(feed.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
      <h2 className="text-lg font-semibold text-slate-800">Calendar subscription</h2>
      <p className="text-sm text-slate-500">
        Subscribe to a live feed of your upcoming visits from Google, Apple, or Outlook. The URL is
        opaque and revocable — anyone with the link can read the next 90 days of non-cancelled
        visits.
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : feed ? (
        <>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs break-all">
              {feed.url}
            </code>
            <button
              type="button"
              onClick={copy}
              className="border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50 text-sm inline-flex items-center gap-2"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              disabled={issue.isPending}
              onClick={() => {
                if (confirm('Rotating revokes the current URL. Anyone subscribed with the old URL will stop getting updates.')) {
                  issue.mutate();
                }
              }}
              className="text-slate-700 text-sm hover:text-sky-600 disabled:opacity-50"
            >
              Rotate
            </button>
            <span className="text-slate-300">•</span>
            <button
              type="button"
              disabled={revoke.isPending}
              onClick={() => {
                if (confirm('Revoke feed? Any current subscribers will stop getting updates.')) {
                  revoke.mutate();
                }
              }}
              className="text-red-600 text-sm hover:text-red-700 disabled:opacity-50"
            >
              Revoke
            </button>
          </div>
          <HowToSubscribe />
        </>
      ) : (
        <>
          <button
            type="button"
            disabled={issue.isPending}
            onClick={() => issue.mutate()}
            className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
          >
            {issue.isPending ? 'Generating…' : 'Generate feed URL'}
          </button>
          <HowToSubscribe />
        </>
      )}
    </div>
  );
}

function HowToSubscribe() {
  return (
    <details className="text-xs text-slate-500 pt-2">
      <summary className="cursor-pointer hover:text-sky-600">How to subscribe</summary>
      <ul className="pl-4 pt-2 space-y-1 list-disc">
        <li>
          <strong>Google Calendar:</strong> Other calendars → + → From URL.
        </li>
        <li>
          <strong>Apple Calendar (macOS):</strong> File → New Calendar Subscription → paste URL.
        </li>
        <li>
          <strong>Outlook.com:</strong> Add calendar → Subscribe from web.
        </li>
      </ul>
      <p className="pt-2">Most clients refresh every few hours — not instant but generally same-day.</p>
    </details>
  );
}
